import React, { useState, useEffect } from 'react'
import { Modal, Form, Button, message, Typography } from 'antd'
import moment from 'moment'
import { calculateOrderTotal } from '../../../InventoryItems/utils/helpers'
import useInventory from '../../../InventoryItems/hooks/useInventory'
import { DATE_FORMAT } from '../../../InventoryItems/utils/constants'
import { mockSuppliers } from '../../../InventoryItems/utils/mockInventoryData'
import styles from './PurchaseOrderForm.module.css'

// Import sub-components
import OrderInformation from './components/OrderInformation'
import OrderItemsTable from './components/OrderItemsTable'
import InventoryItemsTable from './components/InventoryItemsTable'
import SupplierInformation from './components/SupplierInformation'

const { Title } = Typography

const PurchaseOrderForm = ({ visible, onCancel, onSave, title, initialValues }) => {
  const [form] = Form.useForm()
  const { inventory } = useInventory()

  const [orderItems, setOrderItems] = useState([])
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [loading, setLoading] = useState(false)
  const [totalCost, setTotalCost] = useState(0)

  // Set up form initial values when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields()

      if (initialValues) {
        // Format dates for the form
        const formattedValues = {
          ...initialValues,
          orderDate: initialValues.orderDate ? moment(initialValues.orderDate) : moment(),
          expectedDeliveryDate: initialValues.expectedDeliveryDate ? moment(initialValues.expectedDeliveryDate) : null,
          receivedDate: initialValues.receivedDate ? moment(initialValues.receivedDate) : null
        }

        form.setFieldsValue(formattedValues)
        setOrderItems(initialValues.itemsOrdered || [])
        setTotalCost(initialValues.totalCost || 0)

        // Set selected supplier
        const supplier = mockSuppliers.find((s) => s.id === initialValues.supplierId)
        setSelectedSupplier(supplier)
      } else {
        // Set default values for new orders
        form.setFieldsValue({
          orderDate: moment(),
          status: 'Draft',
          itemsOrdered: []
        })
        setOrderItems([])
        setTotalCost(0)
        setSelectedSupplier(null)
      }
    }
  }, [visible, initialValues, form])

  // Update total cost when order items change
  useEffect(() => {
    const total = calculateOrderTotal(orderItems)
    setTotalCost(total)
    form.setFieldsValue({ totalCost: total })
  }, [orderItems, form])

  // Handle supplier change
  const handleSupplierChange = (value) => {
    const supplier = mockSuppliers.find((s) => s.id === value)
    setSelectedSupplier(supplier)

    // Auto-populate expected delivery date based on supplier lead time
    if (supplier) {
      const leadTimeDays = supplier.leadTimeDays || 7
      const expectedDate = moment().add(leadTimeDays, 'days')
      form.setFieldsValue({
        expectedDeliveryDate: expectedDate,
        supplierName: supplier.name
      })
    }
  }

  // Add item to order
  const addOrderItem = (item) => {
    // Check if item already exists in the order
    const existingItemIndex = orderItems.findIndex((i) => i.itemId === item.id)

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      const updatedItems = [...orderItems]
      updatedItems[existingItemIndex].quantity += 1
      updatedItems[existingItemIndex].totalCost =
        updatedItems[existingItemIndex].quantity * updatedItems[existingItemIndex].unitCost
      setOrderItems(updatedItems)
    } else {
      // Add new item
      const newItem = {
        itemId: item.id,
        name: item.name,
        quantity: 1,
        unitCost: item.unitCost,
        totalCost: item.unitCost
      }
      setOrderItems([...orderItems, newItem])
    }
  }

  // Remove item from order
  const removeOrderItem = (itemId) => {
    const updatedItems = orderItems.filter((item) => item.itemId !== itemId)
    setOrderItems(updatedItems)
  }

  // Update item quantity
  const updateItemQuantity = (itemId, quantity) => {
    const updatedItems = orderItems.map((item) => {
      if (item.itemId === itemId) {
        return {
          ...item,
          quantity,
          totalCost: quantity * item.unitCost
        }
      }
      return item
    })
    setOrderItems(updatedItems)
  }

  // Submit form
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true)

        try {
          // Format dates
          const formattedValues = {
            ...values,
            orderDate: values.orderDate ? values.orderDate.format(DATE_FORMAT) : moment().format(DATE_FORMAT),
            expectedDeliveryDate: values.expectedDeliveryDate ? values.expectedDeliveryDate.format(DATE_FORMAT) : null,
            receivedDate: values.receivedDate ? values.receivedDate.format(DATE_FORMAT) : null,
            itemsOrdered: orderItems,
            totalCost: totalCost
          }

          // Add supplier details
          if (selectedSupplier) {
            formattedValues.supplierId = selectedSupplier.id
            formattedValues.supplierName = selectedSupplier.name
          }

          // If editing, preserve the ID
          if (initialValues && initialValues.id) {
            formattedValues.id = initialValues.id
          }

          // Preserve approval chain if editing
          if (initialValues && initialValues.approvalChain) {
            formattedValues.approvalChain = initialValues.approvalChain
          } else {
            formattedValues.approvalChain = []
          }

          // Save the purchase order
          const success = onSave(formattedValues)

          if (success) {
            message.success(`Purchase order ${initialValues ? 'updated' : 'created'} successfully`)
            form.resetFields()
            setOrderItems([])
            setTotalCost(0)
          }
        } catch (error) {
          message.error(`Error ${initialValues ? 'updating' : 'creating'} purchase order: ${error.message}`)
        } finally {
          setLoading(false)
        }
      })
      .catch((info) => {})
  }

  return (
    <Modal
      title={<div className={styles.modalHeader}>{title}</div>}
      visible={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key='cancel' onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </Button>,
        <Button
          key='save'
          type='primary'
          loading={loading}
          onClick={handleSubmit}
          disabled={orderItems.length === 0}
          className={styles.submitButton}
        >
          Save
        </Button>
      ]}
      bodyStyle={{ maxHeight: '80vh', overflow: 'auto' }}
    >
      <Form form={form} layout='vertical' name='purchaseOrderForm'>
        <div className={styles.orderInfoSection}>
          <Title level={5} className={styles.sectionHeader}>
            Order Information
          </Title>
          <OrderInformation form={form} handleSupplierChange={handleSupplierChange} initialValues={initialValues} />
        </div>

        <div className={styles.inventorySection}>
          <Title level={5} className={styles.sectionHeader}>
            Add Items
          </Title>
          <InventoryItemsTable inventory={inventory} addOrderItem={addOrderItem} />
        </div>

        <div className={styles.orderItemsSection}>
          <Title level={5} className={styles.sectionHeader}>
            Order Items
          </Title>
          <OrderItemsTable
            orderItems={orderItems}
            updateItemQuantity={updateItemQuantity}
            removeOrderItem={removeOrderItem}
            totalCost={totalCost}
          />
        </div>
      </Form>

      <div className={styles.supplierSection}>
        <Title level={5} className={styles.sectionHeader}>
          Supplier Information
        </Title>
        <SupplierInformation supplier={selectedSupplier} />
      </div>
    </Modal>
  )
}

export default PurchaseOrderForm
